// admin/src/components/ResultContentEditModal.jsx
import React, { useState, useEffect } from 'react';

const API_ROOT = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const DEGREE_LEVEL_OPTIONS = [
  { value: 'all', label: 'All Levels' },
  { value: 'bachelors', label: "Bachelor's" },
  { value: 'masters', label: "Master's" },
  { value: 'doctorate', label: 'Doctorate' },
  { value: 'graduate', label: 'Graduate' },
  { value: 'certificate', label: 'Certificate' },
  { value: 'associate', label: 'Associate' }
];

export default function ResultContentEditModal({ item, isOpen, onClose, onSave, tenantId, adminSecret }) {
  const isNew = !item?._id;
  
  const [formData, setFormData] = useState({
    tag: '',
    title: '',
    description: '',
    imageUrl: '',
    primaryMindset: '',
    secondaryMindset: '',
    degreeLevelTags: ['all'],
    ctaLabel: '',
    ctaUrl: '',
    enabled: true
  });
  
  const [mindsetTags, setMindsetTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Fetch mindset tags when modal opens
  useEffect(() => {
    if (!isOpen) return;
    
    const fetchMindsetTags = async () => {
      setLoadingTags(true);
      try {
        const res = await fetch(
          `${API_ROOT}/api/tags?tenantId=${encodeURIComponent(tenantId)}&category=mindset`,
          {
            headers: {
              'x-admin-secret': adminSecret || ''
            }
          }
        );
        if (res.ok) {
          const tags = await res.json();
          setMindsetTags(tags);
        } else {
          console.warn('Failed to load mindset tags');
          setMindsetTags([]);
        }
      } catch (err) {
        console.error('Error loading mindset tags:', err);
        setMindsetTags([]);
      } finally {
        setLoadingTags(false);
      }
    };

    fetchMindsetTags();
  }, [isOpen, tenantId, adminSecret]);

  // Reset form when item changes or modal opens
  useEffect(() => {
    if (isOpen && item) {
      setFormData({
        tag: item.tag || '',
        title: item.title || '',
        description: item.description || '',
        imageUrl: item.imageUrl || '',
        primaryMindset: item.primaryMindset || '',
        secondaryMindset: item.secondaryMindset || '',
        degreeLevelTags: Array.isArray(item.degreeLevelTags) && item.degreeLevelTags.length > 0
          ? item.degreeLevelTags
          : ['all'],
        ctaLabel: item.ctaLabel || '',
        ctaUrl: item.ctaUrl || '',
        enabled: item.enabled !== false
      });
    } else if (isOpen && !item) {
      setFormData({
        tag: '',
        title: '',
        description: '',
        imageUrl: '',
        primaryMindset: '',
        secondaryMindset: '',
        degreeLevelTags: ['all'],
        ctaLabel: '',
        ctaUrl: '',
        enabled: true
      });
    }
    setError('');
    setSaving(false);
  }, [isOpen, item]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDegreeLevelToggle = (value) => {
    setFormData(prev => {
      let next = [...prev.degreeLevelTags];

      if (value === 'all') {
        // Clicking "All Levels" replaces everything
        return { ...prev, degreeLevelTags: ['all'] };
      }

      // Remove 'all' if selecting a specific level
      next = next.filter(v => v !== 'all');

      if (next.includes(value)) {
        next = next.filter(v => v !== value);
      } else {
        next.push(value);
      }

      // If nothing selected, fall back to 'all'
      if (next.length === 0) next = ['all'];

      return { ...prev, degreeLevelTags: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.tag.trim()) {
      setError('Tag is required');
      return;
    }
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        tag: formData.tag.trim(),
        title: formData.title.trim(),
        description: formData.description.trim(),
        imageUrl: formData.imageUrl.trim(),
        primaryMindset: formData.primaryMindset || '',
        secondaryMindset: formData.secondaryMindset || '',
        degreeLevelTags: formData.degreeLevelTags,
        ctaLabel: formData.ctaLabel.trim(),
        ctaUrl: formData.ctaUrl.trim()
      };
      
      if (item?._id) {
        payload._id = item._id;
      }

      await onSave(payload);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save result content');
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const isAllSelected = formData.degreeLevelTags.includes('all');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isNew ? 'New Result Content' : 'Edit Result Content'}</h2>
          <button 
            className="modal-close" 
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error" role="alert" style={{ marginBottom: 16 }}>
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="tag">
              Tag (slug) <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              id="tag"
              type="text"
              className="input"
              value={formData.tag}
              onChange={(e) => handleChange('tag', e.target.value)}
              placeholder="e.g., career, community, academics"
              required
              autoFocus
              disabled={!isNew}
            />
            {!isNew && (
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 4 }}>
                Tag cannot be changed after creation
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="title">
              Title <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              id="title"
              type="text"
              className="input"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="e.g., Career Services"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              className="input"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Brief description of this content..."
              rows={4}
              style={{ resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="imageUrl">Image URL</label>
            <input
              id="imageUrl"
              type="url"
              className="input"
              value={formData.imageUrl}
              onChange={(e) => handleChange('imageUrl', e.target.value)}
              placeholder="https://example.edu/images/career-services.jpg"
            />
            {formData.imageUrl && (
              <div style={{ marginTop: 8 }}>
                <img 
                  src={formData.imageUrl} 
                  alt="Preview" 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: 120, 
                    borderRadius: 8,
                    objectFit: 'cover' 
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>

          {/* Degree Level Tags */}
          <div className="form-group">
            <label>Degree Levels</label>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              marginTop: 8,
              padding: '12px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              background: '#f9fafb'
            }}>
              {DEGREE_LEVEL_OPTIONS.map(opt => {
                const isSelected = opt.value === 'all'
                  ? isAllSelected
                  : formData.degreeLevelTags.includes(opt.value);
                const dimmed = opt.value !== 'all' && isAllSelected;

                return (
                  <label
                    key={opt.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      cursor: 'pointer',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      background: isSelected ? '#eff6ff' : 'white',
                      border: `1.5px solid ${isSelected ? '#3b82f6' : '#e5e7eb'}`,
                      fontSize: '0.813rem',
                      fontWeight: isSelected ? 600 : 400,
                      opacity: dimmed ? 0.45 : 1,
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleDegreeLevelToggle(opt.value)}
                      style={{ margin: 0, cursor: 'pointer' }}
                    />
                    {opt.label}
                  </label>
                );
              })}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 6 }}>
              Choose which degree levels see this content. "All Levels" applies universally.
            </div>
          </div>

          {/* CTA Fields */}
          <div className="form-group">
            <label htmlFor="ctaLabel">CTA Button Label</label>
            <input
              id="ctaLabel"
              type="text"
              className="input"
              value={formData.ctaLabel}
              onChange={(e) => handleChange('ctaLabel', e.target.value)}
              placeholder="e.g., Explore Aid Options"
            />
          </div>

          <div className="form-group">
            <label htmlFor="ctaUrl">CTA Button URL</label>
            <input
              id="ctaUrl"
              type="url"
              className="input"
              value={formData.ctaUrl}
              onChange={(e) => handleChange('ctaUrl', e.target.value)}
              placeholder="https://andersonuniversity.edu/admission/financial-aid/"
            />
          </div>

          {/* Primary Mindset */}
          <div className="form-group">
            <label>Primary Mindset Tag</label>
            
            {loadingTags && <div className="muted" style={{ fontSize: '0.85rem' }}>Loading mindset tags…</div>}
            
            {!loadingTags && mindsetTags.length > 0 && (
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: '8px',
                marginTop: 8,
                padding: '12px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                background: '#f9fafb',
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                {mindsetTags.map((tag) => {
                  const isSelected = formData.primaryMindset === tag.slug;
                  return (
                    <label 
                      key={tag._id || tag.slug}
                      title={tag.description || undefined}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'flex-start',
                        gap: 8,
                        cursor: 'pointer',
                        padding: '8px 10px',
                        borderRadius: '8px',
                        background: isSelected ? '#eff6ff' : 'white',
                        border: `1.5px solid ${isSelected ? '#3b82f6' : '#e5e7eb'}`,
                        transition: 'all 0.15s ease',
                        fontSize: '0.813rem'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = '#cbd5e1';
                          e.currentTarget.style.background = '#f8fafc';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.background = 'white';
                        }
                      }}
                    >
                      <input
                        type="radio"
                        name="primaryMindset"
                        checked={isSelected}
                        onChange={() => handleChange('primaryMindset', tag.slug)}
                        style={{ 
                          margin: '2px 0 0 0', 
                          cursor: 'pointer', 
                          flexShrink: 0,
                          width: '16px',
                          height: '16px'
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ 
                          fontWeight: isSelected ? 600 : 500,
                          color: isSelected ? '#1e40af' : '#374151',
                          lineHeight: 1.3,
                          wordBreak: 'break-word'
                        }}>
                          {tag.name}
                        </div>
                        {tag.description && (
                          <div style={{
                            fontSize: '0.72rem',
                            color: isSelected ? '#60a5fa' : '#6b7280',
                            lineHeight: 1.3,
                            marginTop: 2,
                            fontStyle: 'italic'
                          }}>
                            {tag.description}
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            
            {formData.primaryMindset && (
              <button
                type="button"
                onClick={() => handleChange('primaryMindset', '')}
                style={{
                  marginTop: 8,
                  fontSize: '0.75rem',
                  color: '#6b7280',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                Clear selection
              </button>
            )}
            
            <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 8 }}>
              Optional: Main mindset associated with this content
            </div>
          </div>

          {/* Secondary Mindset */}
          <div className="form-group">
            <label>Secondary Mindset Tag</label>
            
            {!loadingTags && mindsetTags.length > 0 && (
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: '8px',
                marginTop: 8,
                padding: '12px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                background: '#f9fafb',
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                {mindsetTags.map((tag) => {
                  const isSelected = formData.secondaryMindset === tag.slug;
                  const isPrimary = formData.primaryMindset === tag.slug;
                  const disabled = isPrimary;
                  
                  return (
                    <label 
                      key={tag._id || tag.slug}
                      title={disabled ? 'Already selected as primary' : (tag.description || undefined)}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'flex-start',
                        gap: 8,
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        padding: '8px 10px',
                        borderRadius: '8px',
                        background: disabled ? '#f3f4f6' : (isSelected ? '#eff6ff' : 'white'),
                        border: `1.5px solid ${isSelected ? '#3b82f6' : '#e5e7eb'}`,
                        transition: 'all 0.15s ease',
                        fontSize: '0.813rem',
                        opacity: disabled ? 0.5 : 1
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected && !disabled) {
                          e.currentTarget.style.borderColor = '#cbd5e1';
                          e.currentTarget.style.background = '#f8fafc';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected && !disabled) {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.background = 'white';
                        }
                      }}
                    >
                      <input
                        type="radio"
                        name="secondaryMindset"
                        checked={isSelected}
                        disabled={disabled}
                        onChange={() => !disabled && handleChange('secondaryMindset', tag.slug)}
                        style={{ 
                          margin: '2px 0 0 0', 
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          flexShrink: 0,
                          width: '16px',
                          height: '16px'
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ 
                          fontWeight: isSelected ? 600 : 500,
                          color: disabled ? '#9ca3af' : (isSelected ? '#1e40af' : '#374151'),
                          lineHeight: 1.3,
                          wordBreak: 'break-word'
                        }}>
                          {tag.name}
                          {isPrimary && <span style={{ marginLeft: 4, fontSize: '0.7rem' }}>(Primary)</span>}
                        </div>
                        {tag.description && !disabled && (
                          <div style={{
                            fontSize: '0.72rem',
                            color: isSelected ? '#60a5fa' : '#6b7280',
                            lineHeight: 1.3,
                            marginTop: 2,
                            fontStyle: 'italic'
                          }}>
                            {tag.description}
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            
            {formData.secondaryMindset && (
              <button
                type="button"
                onClick={() => handleChange('secondaryMindset', '')}
                style={{
                  marginTop: 8,
                  fontSize: '0.75rem',
                  color: '#6b7280',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                Clear selection
              </button>
            )}
            
            <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 8 }}>
              Optional: Secondary mindset (cannot be same as primary)
            </div>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={(e) => handleChange('enabled', e.target.checked)}
              />
              <span>Enabled (visible in quiz results)</span>
            </label>
          </div>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Saving...' : isNew ? 'Create Content' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}